import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classNames from 'classnames';
import { equals } from 'ramda';

import ArrowBackIcon from 'material-design-icons/navigation/svg/production/ic_arrow_back_24px.svg';
import ArrowForwardIcon from 'material-design-icons/navigation/svg/production/ic_arrow_forward_24px.svg';
import ArrowUp from 'material-design-icons/hardware/svg/production/ic_keyboard_arrow_up_24px.svg';
import ArrowDown from 'material-design-icons/hardware/svg/production/ic_keyboard_arrow_down_24px.svg';

import { isSSR } from '../../helpers/view';
import SheetTitle from '../SheetTitle';
import MonthView from './MonthView';
import c from './ReleaseCalendar.less';

const CELL_HEIGHT = 52;
const COLLAPSED_MAX_SHOWN_ROWS = 2;

class ReleaseCalendar extends Component {
  wrapper = {};

  activeCell = {};

  selectableDates = new Map();

  static propTypes = {
    onDateSelect: PropTypes.func.isRequired,
    defaultDate: PropTypes.object,
    releaseDates: PropTypes.array,
    title: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  };

  static defaultProps = {
    defaultDate: moment().format('YYYY-M-DD'),
    releaseDates: [],
    title: '',
  };

  state = {
    collapsible: false,
    collapsed: false,
    placedOnRowNo: 1,
    currentMonth: 0,
    currentYear: moment().year(),
    currentMonthDates: [],
    firstMonth: 0,
    firstYear: 1970,
    lastMonth: 11,
    lastYear: 2100,
    showMonths: false,
    selectedDate: moment(),
  };

  UNSAFE_componentWillMount() {
    this.handleReleaseDatesChange(this.props);
  }

  componentDidMount() {
    window.addEventListener('resize', this.resizeHandler);
    this.setActiveCellRow();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!equals(this.props.releaseDates, nextProps.releaseDates)) {
      this.handleReleaseDatesChange(nextProps);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.selectedDate.isSame(this.state.selectedDate)) {
      this.props.onDateSelect(this.state.selectedDate);

      this.setActiveCellRow();
    }

    // when month was changed
    if (prevState.currentMonth !== this.state.currentMonth) {
      this.setActiveCellRow();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resizeHandler);
  }

  handleReleaseDatesChange(props) {
    const dates = props.releaseDates;

    if (dates.length === 0) return;

    const latestDay = moment(dates[dates.length - 1]);
    const firstDay = moment(dates[0]);
    let selectedDate = props.defaultDate || latestDay;
    let currentMonthDates = dates.filter((date) => date.isSame(selectedDate, 'month'));

    if (currentMonthDates.length === 0) {
      selectedDate = latestDay;
      currentMonthDates = dates.filter((date) => date.isSame(selectedDate, 'month'));
    }

    this.setState({
      currentMonth: selectedDate.month(),
      currentYear: selectedDate.year(),
      lastMonth: latestDay.month(),
      lastYear: latestDay.year(),
      firstMonth: firstDay.month(),
      firstYear: firstDay.year(),
      currentMonthDates,
      selectedDate,
    });

    if (!isSSR()) {
      this.props.onDateSelect(selectedDate);

      setTimeout(() => this.resizeHandler(), 0);

      this.setActiveCellRow();
    }
  }

  get hasNextPeriod() {
    const {
      lastMonth, lastYear, currentYear, currentMonth,
    } = this.state;

    return moment().date(1).month(currentMonth).year(currentYear)
      .isBefore(moment().date(1).month(lastMonth).year(lastYear));
  }

  get hasPreviousPeriod() {
    const {
      firstMonth, firstYear, currentYear, currentMonth,
    } = this.state;

    return moment().date(1).month(currentMonth).year(currentYear)
      .isAfter(moment().date(1).month(firstMonth).year(firstYear));
  }

  setActiveCellRow = () => {
    if (this.activeCell) {
      this.setState({
        placedOnRowNo: Math.ceil(this.activeCell.offsetTop / CELL_HEIGHT),
      });
    }
  };

  handleToggleClick = () => {
    this.setState((state) => ({
      collapsed: !state.collapsed,
    }));
  };

  handleSelectDate = (date) => {
    this.setState({
      selectedDate: date,
    });
  };

  handleSelectMonth = (selectedDate) => {
    this.setState({
      currentMonthDates: this.props.releaseDates.filter((date) => date.isSame(selectedDate, 'month')),
      currentMonth: selectedDate.month(),
      currentYear: selectedDate.year(),
      showMonths: false,
    });
  };

  handleShowPreviousPeriod = () => {
    const { currentMonth, currentYear } = this.state;
    const monthDate = moment()
      .date(1)
      .month(currentMonth)
      .year(currentYear);

    const previousDates = this.props.releaseDates.filter((date) => date.isBefore(monthDate, 'month'));
    const previousMonth = previousDates[previousDates.length - 1];

    this.setState({
      currentMonthDates: previousDates.filter((date) => date.isSame(previousMonth, 'month')),
      currentMonth: previousMonth.month(),
      currentYear: previousMonth.year(),
      placedOnRowNo: 1,
    });
  };

  handleShowNextPeriod = () => {
    const { currentMonth, currentYear } = this.state;
    const monthDate = moment()
      .date(1)
      .month(currentMonth)
      .year(currentYear);

    const nextDates = this.props.releaseDates.filter((date) => date.isAfter(monthDate, 'month'));
    if (nextDates) {
      const nextMonth = nextDates[0];

      this.setState({
        currentMonthDates: nextDates.filter((date) => date.isSame(nextMonth, 'month')),
        currentMonth: nextMonth.month(),
        currentYear: nextMonth.year(),
        placedOnRowNo: 1,
      });
    }
  };

  resizeHandler = () => {
    const shouldBeCollapsed = this.wrapper.scrollHeight > CELL_HEIGHT * COLLAPSED_MAX_SHOWN_ROWS;
    let collapsed = false;

    if ((this.state.collapsed || !this.state.collapsible) && shouldBeCollapsed) {
      collapsed = true;
    }

    this.setState({
      collapsible: shouldBeCollapsed,
      collapsed,
    });
  };

  toggleMonthView = () => {
    this.setState((state) => ({
      showMonths: !state.showMonths,
    }));
  };

  renderDays() {
    const {
      currentMonth, currentMonthDates, selectedDate, currentYear,
    } = this.state;

    if (!selectedDate || currentMonthDates.length === 0) return null;

    const cells = [];
    const monthDate = moment().date(1).month(currentMonth).year(currentYear);
    const lastDayOfMonth = monthDate.clone().endOf('month');
    const currentDate = monthDate.clone().startOf('month');

    while (currentDate.isSameOrBefore(lastDayOfMonth)) {
      const isActive = currentDate.isSame(selectedDate, 'day');
      const isSelectable = currentMonthDates.findIndex((date) => currentDate.isSame(date, 'day')) >= 0;
      const isLastDay = currentDate.isSame(lastDayOfMonth, 'day');

      const cellClasses = classNames(c.dayCell, {
        [c.active]: isActive,
        [c.selectable]: isSelectable && !isActive,
      });

      const props = {};

      if (isActive || isSelectable || isLastDay) {
        props.ref = ((date) => (el) => {
          if (isActive) {
            this.activeCell = el;
          }
          if (isSelectable && !this.selectableDates.has(date.format('YYYY-M-D'))) {
            this.selectableDates.set(date.format('YYYY-M-D'), el);
          }
        })(currentDate.clone());
      }

      cells.push(
        <li
          key={currentDate.format('D')}
          className={cellClasses}
          {...props}
        >
          {
            isSelectable && !isActive ? (
              <a
                href={`/last-added/${currentDate.format('YYYY-M-D')}`}
                onClickCapture={((date) => (e) => {
                  e.preventDefault();
                  this.handleSelectDate(date);
                })(currentDate.clone())}
              >
                {currentDate.format('D')}
              </a>
            ) : (
              currentDate.format('D')
            )
          }
        </li>,
      );
      currentDate.add(1, 'day');
    }

    return cells;
  }

  renderDaysSelector() {
    const { collapsed, collapsible, placedOnRowNo } = this.state;
    const wrapperStyle = {};

    if (collapsible && collapsed && placedOnRowNo > COLLAPSED_MAX_SHOWN_ROWS) {
      wrapperStyle.top = -((placedOnRowNo - COLLAPSED_MAX_SHOWN_ROWS) * CELL_HEIGHT);
    }

    return (
      <Fragment>
        <div className={c.collapseContainer}>
          <ul
            className={classNames(c.cellsWrapper)}
            style={wrapperStyle}
            ref={(el) => {
              this.wrapper = el;
            }}
          >
            {this.renderDays()}
          </ul>
        </div>
        <button type="button" className={c.toggle} onClick={this.handleToggleClick}>
          {
            collapsed ? <ArrowDown /> : <ArrowUp />
          }
        </button>
      </Fragment>
    );
  }

  render() {
    const { title } = this.props;
    const {
      currentMonth, currentYear, showMonths, lastYear, lastMonth, firstYear, firstMonth,
    } = this.state;
    const monthDate = moment().date(1).month(currentMonth).year(currentYear);

    const monthProps = {
      currentYear,
      currentMonth,
      lastYear,
      lastMonth,
      firstYear,
      firstMonth,
      onMonthSelect: this.handleSelectMonth,
    };

    return (
      <div
        className={classNames(c.container, {
          [c.collapsible]: this.state.collapsible,
          [c.collapsed]: this.state.collapsible && this.state.collapsed,
        })}
      >
        <SheetTitle
          title={title}
          rightBlock={
            !showMonths ? (
              <div className={c.navigation}>
                <button
                  type="button"
                  className={c.monthChanger}
                  onClick={this.handleShowPreviousPeriod}
                  disabled={!this.hasPreviousPeriod}
                >
                  <ArrowBackIcon />
                </button>
                <button
                  type="button"
                  className={c.dateView}
                  onClick={this.toggleMonthView}
                >{monthDate.format('MMM YYYY')}
                </button>
                <button
                  type="button"
                  className={c.monthChanger}
                  onClick={this.handleShowNextPeriod}
                  disabled={!this.hasNextPeriod}
                >
                  <ArrowForwardIcon />
                </button>
              </div>
            ) : null
          }
        />
        {
          showMonths ? <MonthView {...monthProps} /> : this.renderDaysSelector()
        }
      </div>
    );
  }
}

export default ReleaseCalendar;
